import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Undo2, Redo2, Save, Type, IndentIncrease, IndentDecrease,
  FileText as TemplateIcon, Plus as PlusIcon, ChevronDown, Image as ImageIcon,
  Eye, Trash2, Minus, Printer,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cadre "papier à en-tête SESAM" affiché en preview (WYSIWYG)
function LetterheadPaper({ editorRef, initialHtml, onInput, onImageClick, headerHtml, onHeaderChange, headerRef, headerOffset, setHeaderOffset }) {
  useEffect(() => {
    if (editorRef.current && initialHtml && !editorRef.current.innerHTML.trim()) {
      editorRef.current.innerHTML = initialHtml;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml]);

  // Injecte le HTML de l'en-tête au montage (sans écraser les modifs utilisateur)
  useEffect(() => {
    if (headerRef.current && headerHtml && !headerRef.current.innerHTML.trim()) {
      headerRef.current.innerHTML = headerHtml;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerHtml]);

  // Intercepter les clics sur images pour afficher la toolbar + ajouter bouton delete sur les blocs
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const handler = (e) => {
      // Gestion suppression de bloc via le bouton ✕
      if (e.target && e.target.classList && e.target.classList.contains('block-delete-btn')) {
        e.preventDefault();
        e.stopPropagation();
        const block = e.target.closest('[data-block]');
        if (block && block.parentElement === el) {
          block.remove();
          onInput(el.innerHTML || '');
        }
        return;
      }
      if (e.target && e.target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        onImageClick(e.target);
      } else {
        onImageClick(null);
      }
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intercepter les clics sur images dans l'en-tête → toolbar aussi
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const handler = (e) => {
      if (e.target && e.target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        onImageClick(e.target);
      }
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag & drop de l'en-tête entier via la poignée
  const startDrag = (e) => {
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY, ox: headerOffset.x, oy: headerOffset.y };
    const move = (ev) => {
      setHeaderOffset({ x: start.ox + (ev.clientX - start.x), y: start.oy + (ev.clientY - start.y) });
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div className="bg-white shadow-lg mx-auto relative" style={{ width: '210mm', minHeight: '297mm', padding: '18mm' }}>
      {/* En-tête libre (contentEditable, déplaçable) */}
      <div className="group relative" style={{ transform: `translate(${headerOffset.x}px, ${headerOffset.y}px)`, marginBottom: '20px' }}>
        {/* Poignée de déplacement + reset (visible au survol) */}
        <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button
            type="button"
            onMouseDown={startDrag}
            className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded shadow cursor-move select-none"
            title="Glisser pour déplacer l'en-tête"
            data-testid="header-drag-handle"
          >
            ⇔ Déplacer
          </button>
          <button
            type="button"
            onClick={() => setHeaderOffset({ x: 0, y: 0 })}
            className="bg-slate-500 text-white text-xs font-bold px-2 py-1 rounded shadow"
            title="Réinitialiser la position"
            data-testid="header-reset"
          >
            ↺
          </button>
        </div>
        <div
          ref={headerRef}
          contentEditable
          suppressContentEditableWarning
          onInput={() => onHeaderChange(headerRef.current?.innerHTML || '')}
          className="outline-none focus:ring-2 focus:ring-blue-200 rounded p-1 -m-1"
          data-testid="doc-editor-header"
        />
      </div>

      {/* Corps éditable + style hover pour boutons delete */}
      <style dangerouslySetInnerHTML={{ __html:
        ".doc-editor-body-wrap [data-block]{position:relative;padding:4px 30px 4px 4px;border-radius:4px;}" +
        ".doc-editor-body-wrap [data-block]:hover{background:rgba(59,130,246,0.05);}" +
        ".doc-editor-body-wrap .block-delete-btn{position:absolute;top:4px;right:4px;width:22px;height:22px;border-radius:4px;background:#ef4444;color:white;font-size:14px;line-height:22px;text-align:center;cursor:pointer;opacity:0;transition:opacity .15s;user-select:none;border:none;}" +
        ".doc-editor-body-wrap [data-block]:hover .block-delete-btn{opacity:1;}"
      }} />
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onInput(editorRef.current?.innerHTML || '')}
        className="min-h-[550px] outline-none text-[11pt] leading-relaxed text-slate-900 focus:ring-2 focus:ring-blue-200 rounded p-2 -m-2 doc-editor-body-wrap"
        style={{ fontFamily: 'Calibri, Helvetica, Arial, sans-serif' }}
        data-testid="doc-editor-body"
      />

      {/* Signature en bas à droite (nom seul, sans cachet) */}
      <div className="text-right mt-10">
        <div className="text-sm font-bold text-slate-900">SIDYCK-DJALLE Amadou</div>
      </div>
    </div>
  );
}

// Toolbar flottante affichée quand une image est sélectionnée
function ImageFloatingToolbar({ img, editorRef, onChange, onClose }) {
  if (!img) return null;
  const rect = img.getBoundingClientRect();
  const parent = editorRef.current?.getBoundingClientRect();
  if (!parent) return null;

  const setSize = (percent) => {
    img.style.width = `${percent}%`;
    img.style.height = 'auto';
    onChange();
  };
  const deleteImg = () => {
    img.remove();
    onClose();
    onChange();
  };
  const align = (a) => {
    img.style.display = 'block';
    if (a === 'left') { img.style.marginLeft = '0'; img.style.marginRight = 'auto'; }
    else if (a === 'right') { img.style.marginLeft = 'auto'; img.style.marginRight = '0'; }
    else if (a === 'center') { img.style.marginLeft = 'auto'; img.style.marginRight = 'auto'; }
    onChange();
  };

  return (
    <div
      className="fixed z-[70] bg-white border-2 border-blue-400 rounded-lg shadow-2xl px-2 py-1.5 flex items-center gap-1"
      style={{ top: rect.top - 44, left: rect.left, minWidth: 380 }}
      data-testid="image-toolbar"
    >
      <span className="text-xs text-slate-500 font-semibold px-1">Taille :</span>
      <button className="text-xs px-2 py-0.5 rounded border hover:bg-blue-50" onClick={() => setSize(25)} data-testid="img-size-25">25%</button>
      <button className="text-xs px-2 py-0.5 rounded border hover:bg-blue-50" onClick={() => setSize(50)} data-testid="img-size-50">50%</button>
      <button className="text-xs px-2 py-0.5 rounded border hover:bg-blue-50" onClick={() => setSize(75)} data-testid="img-size-75">75%</button>
      <button className="text-xs px-2 py-0.5 rounded border hover:bg-blue-50" onClick={() => setSize(100)} data-testid="img-size-100">100%</button>
      <div className="w-px h-4 bg-slate-300 mx-1" />
      <span className="text-xs text-slate-500 font-semibold px-1">Aligner :</span>
      <button className="p-1 rounded hover:bg-blue-50" onClick={() => align('left')} title="Gauche" data-testid="img-align-left"><AlignLeft className="h-3.5 w-3.5"/></button>
      <button className="p-1 rounded hover:bg-blue-50" onClick={() => align('center')} title="Centre" data-testid="img-align-center"><AlignCenter className="h-3.5 w-3.5"/></button>
      <button className="p-1 rounded hover:bg-blue-50" onClick={() => align('right')} title="Droite" data-testid="img-align-right"><AlignRight className="h-3.5 w-3.5"/></button>
      <div className="w-px h-4 bg-slate-300 mx-1" />
      <button
        className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1 font-semibold"
        onClick={deleteImg}
        data-testid="img-delete"
      >
        <Trash2 className="h-3.5 w-3.5"/> Supprimer
      </button>
    </div>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick, active, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={label}
      data-testid={testid}
      className={`p-1.5 rounded hover:bg-slate-200 transition ${active ? 'bg-slate-200' : ''}`}
    >
      <Icon className="h-4 w-4 text-slate-700" />
    </button>
  );
}

export default function RemplirDocumentDialog({ open, onClose, onSaved, existingDoc = null }) {
  const editorRef = useRef(null);
  const headerRef = useRef(null);
  const DEFAULT_HEADER_HTML = `
    <div style="display:table; width:100%;">
      <div style="display:table-cell; vertical-align:top; color:#1B3170; font-size:11pt; line-height:1.6; font-family:Helvetica,Arial,sans-serif;">
        Avenue Boganda, Centre-ville, Bangui –<br/>
        République Centrafricaine<br/>
        Numéro : 75 50 37 02 / 74 36 11 36<br/>
        Email : sesambangui@gmail.com<br/>
        Site : www.sesamrca.com<br/>
        N° Ident. : M0732100100165B
      </div>
      <div style="display:table-cell; vertical-align:top; text-align:right;">
        <img src="/logo_sesam.png" alt="SESAM" style="width:110px; height:auto; display:inline-block;"/>
        <div style="color:#1B3170; font-size:11pt; font-weight:700; margin-top:3px;">Agence de communication</div>
      </div>
    </div>
  `.trim();
  const [nom, setNom] = useState('');
  const [type, setType] = useState('Attestation');
  const [contenuHtml, setContenuHtml] = useState('');
  const [enteteHtml, setEnteteHtml] = useState('');
  const [headerOffset, setHeaderOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [fontSize, setFontSize] = useState('3'); // 1..7 pour execCommand
  const [templates, setTemplates] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [savedDocId, setSavedDocId] = useState(null); // pour permettre l'export DOCX post-save
  const [selectedImg, setSelectedImg] = useState(null); // image cliquée dans l'éditeur
  const [previewLoading, setPreviewLoading] = useState(false);

  // Charger templates + clients à l'ouverture
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [t, c] = await Promise.all([
          axios.get(`${API}/api/company/documents/templates`),
          axios.get(`${API}/api/company/documents/variables/clients`),
        ]);
        setTemplates(t.data?.templates || []);
        setClientsList(c.data?.clients || []);
      } catch { /* ignore */ }
    })();
  }, [open]);

  useEffect(() => {
    if (open) {
      if (existingDoc) {
        setNom(existingDoc.nom || '');
        setType(existingDoc.type || 'Attestation');
        setContenuHtml(existingDoc.contenu_html || '');
        setEnteteHtml(existingDoc.entete_html || DEFAULT_HEADER_HTML);
        setHeaderOffset(existingDoc.header_offset || { x: 0, y: 0 });
        setSavedDocId(existingDoc.id);
        // Réinjecter le HTML dans le contenteditable header
        setTimeout(() => {
          if (headerRef.current) headerRef.current.innerHTML = existingDoc.entete_html || DEFAULT_HEADER_HTML;
        }, 50);
      } else {
        setNom('');
        setType('Attestation');
        setContenuHtml(`<h2 style="text-align:center;">ATTESTATION</h2><p><br/></p><p>Je soussigné, ..................................................................,</p><p>atteste que ....................................................................</p><p>...................................................................................</p><p>...................................................................................</p><p>...................................................................................</p><p><br/></p><p>Fait à Bangui, le ...........................................</p>`);
        setEnteteHtml(DEFAULT_HEADER_HTML);
        setHeaderOffset({ x: 0, y: 0 });
        setSavedDocId(null);
        setTimeout(() => {
          if (headerRef.current) headerRef.current.innerHTML = DEFAULT_HEADER_HTML;
        }, 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingDoc]);

  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    setContenuHtml(editorRef.current?.innerHTML || '');
  };

  const wrapAsBlock = (html) =>
    `<div data-block>${html}<button contenteditable="false" class="block-delete-btn" title="Supprimer ce bloc">×</button></div>`;

  const addParagraph = () => {
    const html = wrapAsBlock('<p>Nouveau paragraphe — cliquez pour modifier…</p>');
    editorRef.current?.focus();
    // Insérer après le bloc courant ou à la fin
    editorRef.current.insertAdjacentHTML('beforeend', html);
    setContenuHtml(editorRef.current?.innerHTML || '');
    toast.success('Paragraphe ajouté');
  };

  const addTitle = () => {
    const html = wrapAsBlock('<h2 style="color:#1B3170;">Nouveau titre</h2>');
    editorRef.current?.focus();
    editorRef.current.insertAdjacentHTML('beforeend', html);
    setContenuHtml(editorRef.current?.innerHTML || '');
  };

  const loadTemplate = (tpl) => {
    if (!tpl) return;
    if (contenuHtml && !window.confirm(`Charger le modèle "${tpl.nom}" ? Le contenu actuel sera remplacé.`)) return;
    setNom(nom || tpl.nom);
    setType(tpl.type || 'Attestation');
    // Injecter la date du jour automatiquement dans les templates
    const dateFr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = String(tpl.html || '').replace(/\{\{date\}\}/g, dateFr);
    setContenuHtml(html);
    if (editorRef.current) editorRef.current.innerHTML = html;
  };

  const insertAtCursor = (html) => {
    editorRef.current?.focus();
    // Utilise l'API selection pour insérer au curseur
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const el = document.createElement('span');
      el.innerHTML = html;
      const frag = document.createDocumentFragment();
      let node, lastNode;
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node);
      }
      range.insertNode(frag);
      if (lastNode) {
        const newRange = range.cloneRange();
        newRange.setStartAfter(lastNode);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
    } else {
      // Fallback : ajouter à la fin
      if (editorRef.current) editorRef.current.innerHTML += html;
    }
    setContenuHtml(editorRef.current?.innerHTML || '');
  };

  const insertVariable = (v) => {
    const dateFr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const generateNumeroDossier = () => {
      const y = new Date().getFullYear();
      const rand = Math.floor(1000 + Math.random() * 9000);
      return `DOC-${y}-${rand}`;
    };
    if (v === 'date') insertAtCursor(dateFr);
    else if (v === 'numero_dossier') insertAtCursor(generateNumeroDossier());
    else if (v === 'lieu') insertAtCursor('Bangui');
    else if (v === 'expediteur') insertAtCursor('SIDYCK-DJALLE Amadou');
  };

  const insertClientField = (client, field) => {
    let val = '';
    if (field === 'nom') val = client.nom || '';
    else if (field === 'email') val = client.email || '';
    else if (field === 'telephone') val = client.telephone || '';
    else if (field === 'adresse') val = client.adresse || '';
    else if (field === 'nif') val = client.nif || '';
    if (val) insertAtCursor(val);
  };

  const fileInputRef = useRef(null);

  const handleImageFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Merci de sélectionner une image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop volumineuse (max 5 MB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl === 'string') {
        insertAtCursor(`<img src="${dataUrl}" style="max-width:100%; height:auto; margin:8px 0;" />`);
        toast.success('Image insérée');
      }
    };
    reader.readAsDataURL(file);
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const save = async () => {
    if (!nom.trim()) { toast.error('Veuillez saisir un nom pour le document'); return; }
    setSaving(true);
    try {
      const body = {
        nom: nom.trim(), type, contenu_html: contenuHtml, description: '',
        entete_html: enteteHtml, header_offset: headerOffset,
      };
      const url = existingDoc || savedDocId
        ? `${API}/api/company/documents/${existingDoc?.id || savedDocId}/update-from-editor`
        : `${API}/api/company/documents/create-from-editor`;
      const method = (existingDoc || savedDocId) ? 'put' : 'post';
      const r = await axios[method](url, body);
      if (r.data?.success) {
        toast.success((existingDoc || savedDocId) ? 'Document mis à jour' : 'Document enregistré');
        setSavedDocId(r.data.document?.id || savedDocId);
        onSaved?.(r.data.document);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur serveur');
    } finally {
      setSaving(false);
    }
  };

  const exportDocx = async () => {
    let id = existingDoc?.id || savedDocId;
    if (!id) {
      // Sauvegarder d'abord puis exporter
      await save();
      id = savedDocId;
      if (!id) return;
    }
    try {
      const r = await axios.get(`${API}/api/company/documents/${id}/docx`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safeName = (nom || 'document').replace(/[^a-zA-Z0-9_\-]/g, '_');
      link.download = `${safeName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Export Word téléchargé');
    } catch { toast.error('Erreur export DOCX'); }
  };

  const previewPdf = async () => {
    setPreviewLoading(true);
    try {
      const body = {
        nom: nom || 'Aperçu', type, contenu_html: contenuHtml, description: '',
        entete_html: enteteHtml, header_offset: headerOffset,
      };
      const r = await axios.post(`${API}/api/company/documents/preview-pdf`, body, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch { toast.error('Erreur aperçu PDF'); }
    finally { setPreviewLoading(false); }
  };

  const printDoc = async () => {
    // Génère le PDF côté serveur puis ouvre une fenêtre invisible qui lance l'impression
    setPreviewLoading(true);
    try {
      const body = {
        nom: nom || 'Document', type, contenu_html: contenuHtml, description: '',
        entete_html: enteteHtml, header_offset: headerOffset,
      };
      const r = await axios.post(`${API}/api/company/documents/preview-pdf`, body, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      // Ouvrir dans un onglet dédié et déclencher le dialogue d'impression
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w) {
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch (e) { /* silencieux */ }
        });
        // Fallback : certains navigateurs n'émettent pas load pour les PDF natifs
        setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* silencieux */ } }, 1500);
      } else {
        toast.error('Impossible d\'ouvrir la fenêtre d\'impression (popup bloqué ?)');
      }
    } catch { toast.error('Erreur impression'); }
    finally { setPreviewLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-6xl h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="remplir-document-dialog">
        {/* Input caché pour upload d'image */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { handleImageFile(e.target.files?.[0]); if (e.target) e.target.value = ''; }}
          data-testid="doc-editor-image-input"
        />
        <DialogHeader className="bg-[#1B3170] text-white px-4 py-3 flex-shrink-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <Type className="h-5 w-5" /> {existingDoc ? 'Modifier le document' : 'Remplir un document'}
          </DialogTitle>
        </DialogHeader>

        {/* Nom + Type + Modèles + Insérer variables */}
        <div className="px-4 py-2 border-b flex items-center gap-2 flex-shrink-0 bg-slate-50 flex-wrap">
          <Input
            placeholder="Nom du document (ex: Attestation - Client X)"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            className="max-w-md"
            data-testid="doc-editor-nom"
          />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-40" data-testid="doc-editor-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Attestation">Attestation</SelectItem>
              <SelectItem value="Lettre">Lettre</SelectItem>
              <SelectItem value="Note">Note</SelectItem>
              <SelectItem value="Légal">Légal</SelectItem>
              <SelectItem value="Autre">Autre</SelectItem>
            </SelectContent>
          </Select>

          {/* Menu Modèles */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="btn-menu-templates">
                <TemplateIcon className="h-4 w-4" /> Modèles <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel>Charger un modèle</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {templates.map(tpl => (
                <DropdownMenuItem
                  key={tpl.id}
                  onClick={() => loadTemplate(tpl)}
                  data-testid={`tpl-${tpl.id}`}
                  className="flex flex-col items-start gap-0.5 py-2"
                >
                  <div className="font-semibold text-sm text-slate-900">{tpl.nom}</div>
                  <div className="text-xs text-slate-500">{tpl.description}</div>
                </DropdownMenuItem>
              ))}
              {templates.length === 0 && (
                <DropdownMenuItem disabled>Aucun modèle disponible</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Menu Insérer (variables) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="btn-menu-insert">
                <PlusIcon className="h-4 w-4" /> Insérer <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 max-h-96 overflow-auto">
              <DropdownMenuLabel>Variables système</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={triggerImageUpload} data-testid="var-image" className="text-emerald-700 font-semibold">
                <ImageIcon className="h-4 w-4 mr-2" /> Insérer une image (fichier)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => insertVariable('date')} data-testid="var-date">
                📅 Date du jour
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => insertVariable('lieu')} data-testid="var-lieu">
                📍 Lieu (Bangui)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => insertVariable('numero_dossier')} data-testid="var-numero">
                🔢 Numéro de dossier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => insertVariable('expediteur')} data-testid="var-expediteur">
                ✍️ Expéditeur (Directeur)
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Client existant</DropdownMenuLabel>
              {clientsList.length === 0 && (
                <DropdownMenuItem disabled className="text-xs text-slate-500">Aucun client enregistré</DropdownMenuItem>
              )}
              {clientsList.map(c => (
                <div key={c.id} className="px-2 py-1">
                  <div className="font-semibold text-xs text-slate-800 mb-1">{c.nom}</div>
                  <div className="flex flex-wrap gap-1">
                    <button className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-100" onClick={() => insertClientField(c, 'nom')}>Nom</button>
                    {c.adresse && <button className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-100" onClick={() => insertClientField(c, 'adresse')}>Adresse</button>}
                    {c.telephone && <button className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-100" onClick={() => insertClientField(c, 'telephone')}>Tél</button>}
                    {c.email && <button className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-100" onClick={() => insertClientField(c, 'email')}>Email</button>}
                    {c.nif && <button className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-100" onClick={() => insertClientField(c, 'nif')}>NIF</button>}
                  </div>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Toolbar */}
        <div className="px-4 py-2 border-b flex items-center gap-1 flex-wrap flex-shrink-0 bg-white">
          <Button size="sm" variant="outline" onClick={addParagraph} className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50 h-8 text-xs" data-testid="tb-add-paragraph">
            <PlusIcon className="h-3 w-3" /> Paragraphe
          </Button>
          <Button size="sm" variant="outline" onClick={addTitle} className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50 h-8 text-xs" data-testid="tb-add-title">
            <PlusIcon className="h-3 w-3" /> Titre
          </Button>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <ToolbarBtn icon={Undo2} label="Annuler" onClick={() => exec('undo')} testid="tb-undo" />
          <ToolbarBtn icon={Redo2} label="Rétablir" onClick={() => exec('redo')} testid="tb-redo" />
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <select
            className="text-sm border rounded px-2 py-1"
            value={fontSize}
            onChange={(e) => { setFontSize(e.target.value); exec('fontSize', e.target.value); }}
            title="Taille"
            data-testid="tb-size"
          >
            {['1','2','3','4','5','6','7'].map(s => <option key={s} value={s}>Taille {s}</option>)}
          </select>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <ToolbarBtn icon={Bold} label="Gras" onClick={() => exec('bold')} testid="tb-bold" />
          <ToolbarBtn icon={Italic} label="Italique" onClick={() => exec('italic')} testid="tb-italic" />
          <ToolbarBtn icon={Underline} label="Souligné" onClick={() => exec('underline')} testid="tb-underline" />
          <input type="color" onChange={(e) => exec('foreColor', e.target.value)} title="Couleur" className="w-7 h-7 rounded cursor-pointer" data-testid="tb-color"/>
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <ToolbarBtn icon={AlignLeft} label="Aligner gauche" onClick={() => exec('justifyLeft')} testid="tb-alignleft" />
          <ToolbarBtn icon={AlignCenter} label="Centrer" onClick={() => exec('justifyCenter')} testid="tb-aligncenter" />
          <ToolbarBtn icon={AlignRight} label="Aligner droite" onClick={() => exec('justifyRight')} testid="tb-alignright" />
          <ToolbarBtn icon={AlignJustify} label="Justifier" onClick={() => exec('justifyFull')} testid="tb-alignjustify" />
          <div className="w-px h-6 bg-slate-300 mx-1" />
          <ToolbarBtn icon={List} label="Liste à puces" onClick={() => exec('insertUnorderedList')} testid="tb-ul" />
          <ToolbarBtn icon={ListOrdered} label="Liste numérotée" onClick={() => exec('insertOrderedList')} testid="tb-ol" />
          <ToolbarBtn icon={IndentDecrease} label="Diminuer retrait" onClick={() => exec('outdent')} testid="tb-outdent" />
          <ToolbarBtn icon={IndentIncrease} label="Augmenter retrait" onClick={() => exec('indent')} testid="tb-indent" />
        </div>

        {/* Zone d'édition (papier à en-tête) */}
        <div className="flex-1 overflow-auto bg-slate-100 p-6 relative">
          <LetterheadPaper
            editorRef={editorRef}
            initialHtml={contenuHtml}
            onInput={(html) => setContenuHtml(html)}
            headerRef={headerRef}
            headerHtml={enteteHtml}
            onHeaderChange={(html) => setEnteteHtml(html)}
            headerOffset={headerOffset}
            setHeaderOffset={setHeaderOffset}
            onImageClick={(img) => {
              setSelectedImg(img);
              if (img) {
                img.style.outline = '2px solid #3b82f6';
                img.style.outlineOffset = '2px';
              }
              // Nettoyer l'outline des autres images
              editorRef.current?.querySelectorAll('img').forEach((i) => {
                if (i !== img) { i.style.outline = ''; i.style.outlineOffset = ''; }
              });
              headerRef.current?.querySelectorAll('img').forEach((i) => {
                if (i !== img) { i.style.outline = ''; i.style.outlineOffset = ''; }
              });
            }}
          />
          <ImageFloatingToolbar
            img={selectedImg}
            editorRef={editorRef}
            onChange={() => setContenuHtml(editorRef.current?.innerHTML || '')}
            onClose={() => {
              if (selectedImg) { selectedImg.style.outline = ''; selectedImg.style.outlineOffset = ''; }
              setSelectedImg(null);
            }}
          />
        </div>

        {/* Footer : boutons Aperçu + Word + Enregistrer */}
        <div className="border-t px-4 py-3 flex items-center justify-end gap-2 bg-white flex-shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="doc-editor-cancel">Annuler</Button>
          <Button
            variant="outline"
            className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={previewPdf}
            disabled={previewLoading || saving}
            data-testid="doc-editor-preview"
            title="Ouvrir un aperçu PDF dans un nouvel onglet"
          >
            <Eye className="h-4 w-4" />
            {previewLoading ? 'Chargement...' : 'Aperçu PDF'}
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={printDoc}
            disabled={previewLoading || saving}
            data-testid="doc-editor-print"
            title="Imprimer le document"
          >
            <Printer className="h-4 w-4" />
            Imprimer
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={exportDocx}
            disabled={saving}
            data-testid="doc-editor-docx"
            title="Exporter au format Word (.docx)"
          >
            <TemplateIcon className="h-4 w-4" />
            Word (.docx)
          </Button>
          <Button
            className="bg-[#1B3170] hover:bg-[#0B1E42] gap-2"
            onClick={save}
            disabled={saving}
            data-testid="doc-editor-save"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
